/**
 * Cognito access for the capture client. Direct SDK flow through
 * `amazon-cognito-identity-js`, matching the sibling portal
 * (`apps/classification-ui/src/components/auth/cognitoService.jsx`): no
 * hosted UI, no OAuth redirect, no callback URL. Both clients sign in
 * against the same pool the same way (PRIN-SC-4, one identity system).
 *
 * Token storage is the SDK's own localStorage keys, which is what makes a
 * reload survivable on a phone: `restoreSession` reads them back and the
 * SDK refreshes with the stored refresh token when the ID token has aged
 * out. This module never reads the refresh token itself and never logs.
 *
 * Pool id, client id and region come from Vite env vars declared in
 * `src/vite-env.d.ts`. A token minted from any other pool or client is
 * rejected by the API Gateway JWT authorizer, so these are configuration,
 * never constants in source.
 */

import './polyfills';

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { readTokenExpiry, userFromClaims, decodeJwtClaims } from './token';
import type { AuthSession } from './types';

export class AuthConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthConfigError';
  }
}

let cachedPool: CognitoUserPool | null = null;

export function getUserPool(): CognitoUserPool {
  if (cachedPool) return cachedPool;

  const userPoolId = import.meta.env.VITE_CAPTURE_COGNITO_USER_POOL_ID;
  const clientId = import.meta.env.VITE_CAPTURE_COGNITO_CLIENT_ID;
  const region = import.meta.env.VITE_CAPTURE_COGNITO_REGION;

  const missing = [
    userPoolId ? null : 'VITE_CAPTURE_COGNITO_USER_POOL_ID',
    clientId ? null : 'VITE_CAPTURE_COGNITO_CLIENT_ID',
    region ? null : 'VITE_CAPTURE_COGNITO_REGION',
  ].filter((name): name is string => name !== null);

  if (missing.length > 0) {
    throw new AuthConfigError(`Sign-in is not configured for this build. Missing: ${missing.join(', ')}.`);
  }

  cachedPool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: clientId,
    // Same value the SDK derives from the pool id, stated explicitly so a
    // build pointed at another region fails on its own configuration
    // rather than on a silent default.
    endpoint: `https://cognito-idp.${region}.amazonaws.com/`,
  });
  return cachedPool;
}

function sessionFrom(session: CognitoUserSession): AuthSession | null {
  const idToken = session.getIdToken().getJwtToken();
  const claims = decodeJwtClaims(idToken);
  if (!claims) return null;
  return {
    idToken,
    expiresAt: readTokenExpiry(idToken),
    user: userFromClaims(claims),
  };
}

/** A sign-in step that needs one more answer from the operator. `begin`
 * runs once when the step opens (MFA setup issues the key there) and
 * `submit` answers it with the operator's input. */
export interface AuthChallenge {
  kind: 'new-password' | 'mfa-setup' | 'mfa-code';
  /** Setup only: issues the authenticator key. Call once; each call
   * replaces the key and the Cognito session the verify step answers on. */
  begin?: () => Promise<string>;
  submit: (input: string) => Promise<SignInResult>;
}

export type SignInResult =
  | { status: 'success'; session: AuthSession }
  | { status: 'challenge'; challenge: AuthChallenge };

/** otpauth URI an authenticator app reads from the setup QR code. */
export function totpUri(email: string, secret: string, issuer: string): string {
  const label = encodeURIComponent(`${issuer}:${email}`);
  return `otpauth://totp/${label}?secret=${secret}&issuer=${encodeURIComponent(issuer)}`;
}

function unreadableToken(): Error {
  return new Error('Cognito returned a token this client could not read.');
}

/** The callbacks shared by the password step and the new-password step,
 * which can each end in a session or in any of the three challenges. The
 * `CognitoUser` instance passes between steps as the object itself: a JSON
 * copy loses the session methods the next step calls. */
function challengeCallbacks(
  cognitoUser: CognitoUser,
  issuer: string,
  email: string,
  resolve: (result: SignInResult) => void,
  reject: (error: unknown) => void,
) {
  const challenge = (value: AuthChallenge) => resolve({ status: 'challenge', challenge: value });
  const codeStep = (): AuthChallenge => ({
    kind: 'mfa-code',
    submit: (code) =>
      new Promise<SignInResult>((res, rej) => {
        cognitoUser.sendMFACode(code, challengeCallbacks(cognitoUser, issuer, email, res, rej), 'SOFTWARE_TOKEN_MFA');
      }),
  });
  return {
    onSuccess: (session: CognitoUserSession) => {
      const parsed = sessionFrom(session);
      if (!parsed) {
        reject(unreadableToken());
        return;
      }
      resolve({ status: 'success', session: parsed });
    },
    onFailure: (error: unknown) => reject(error),
    newPasswordRequired: () =>
      challenge({
        kind: 'new-password',
        submit: (newPassword) =>
          new Promise<SignInResult>((res, rej) => {
            cognitoUser.completeNewPasswordChallenge(
              newPassword,
              {},
              challengeCallbacks(cognitoUser, issuer, email, res, rej),
            );
          }),
      }),
    // Cognito raises `totpRequired` when software-token MFA is the
    // outstanding factor and `mfaRequired` for SMS. The pool allows
    // software tokens only, so both are answered as SOFTWARE_TOKEN_MFA.
    totpRequired: () => challenge(codeStep()),
    mfaRequired: () => challenge(codeStep()),
    mfaSetup: () =>
      challenge({
        kind: 'mfa-setup',
        begin: () =>
          new Promise<string>((res, rej) => {
            cognitoUser.associateSoftwareToken({
              associateSecretCode: (secret) => res(secret),
              onFailure: (error) => rej(error),
            });
          }),
        submit: (code) =>
          new Promise<SignInResult>((res, rej) => {
            cognitoUser.verifySoftwareToken(code, issuer, {
              onSuccess: (session) => {
                const parsed = sessionFrom(session as CognitoUserSession);
                if (!parsed) {
                  rej(unreadableToken());
                  return;
                }
                // Make the authenticator the preferred factor so every
                // later sign-in asks for a code. The session is already
                // issued, so the callback resolves it either way.
                cognitoUser.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, () =>
                  res({ status: 'success', session: parsed }),
                );
              },
              onFailure: (error) => rej(error),
            });
          }),
      }),
  };
}

/** Authenticates against the pool. Resolves with the session the API
 * Gateway authorizer accepts, or with the next challenge when Cognito asks
 * for a new password or an authenticator code. Rejects with the SDK's own
 * error otherwise; callers map those with `signInErrorMessage`. `issuer`
 * is the app name an authenticator app lists the account under. */
export function signIn(email: string, password: string, issuer: string): Promise<SignInResult> {
  const pool = getUserPool();
  const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise<SignInResult>((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, challengeCallbacks(cognitoUser, issuer, email, resolve, reject));
  });
}

/** Reads the stored session back after a reload or a relaunch from the
 * home screen. `getSession` refreshes with the stored refresh token when
 * the ID token has expired, so a signed-in operator returning to the app
 * keeps working. Resolves null when there is nothing usable, which is a
 * normal first launch, not an error. */
export function restoreSession(): Promise<AuthSession | null> {
  let pool: CognitoUserPool;
  try {
    pool = getUserPool();
  } catch {
    return Promise.resolve(null);
  }

  const cognitoUser = pool.getCurrentUser();
  if (!cognitoUser) return Promise.resolve(null);

  return new Promise<AuthSession | null>((resolve) => {
    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(sessionFrom(session));
    });
  });
}

/** Forces a token refresh from the stored refresh token. Used when a call
 * comes back 401 with a token the client still believed in. Resolves null
 * when the refresh token is itself gone or rejected, which is the
 * unrecoverable case: the caller signs the operator out. */
export function refreshSession(): Promise<AuthSession | null> {
  let pool: CognitoUserPool;
  try {
    pool = getUserPool();
  } catch {
    return Promise.resolve(null);
  }

  const cognitoUser = pool.getCurrentUser();
  if (!cognitoUser) return Promise.resolve(null);

  return new Promise<AuthSession | null>((resolve) => {
    cognitoUser.getSession((sessionError: Error | null, session: CognitoUserSession | null) => {
      if (sessionError || !session) {
        resolve(null);
        return;
      }
      cognitoUser.refreshSession(session.getRefreshToken(), (refreshError, refreshed: CognitoUserSession | null) => {
        if (refreshError || !refreshed) {
          resolve(null);
          return;
        }
        resolve(sessionFrom(refreshed));
      });
    });
  });
}

/** Clears the SDK's stored tokens, including the refresh token. */
export function signOut(): void {
  let pool: CognitoUserPool;
  try {
    pool = getUserPool();
  } catch {
    return;
  }
  pool.getCurrentUser()?.signOut();
}

/** Turns a Cognito failure into something an operator in a warehouse aisle
 * can act on. The submitted password never appears in the result, and the
 * error is not logged anywhere. */
export function signInErrorMessage(error: unknown): string {
  if (error instanceof AuthConfigError) {
    return error.message;
  }
  const name = (error as { code?: string; name?: string } | null)?.code ?? (error as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAuthorizedException':
      return 'That email and password combination was not accepted.';
    case 'UserNotFoundException':
      return 'No account exists for that email.';
    case 'UserNotConfirmedException':
      return 'This account is not confirmed yet. Confirm it in the partner portal first.';
    case 'PasswordResetRequiredException':
      return 'This account needs a password reset. Reset it in the partner portal, then sign in here.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many sign-in attempts. Wait a moment and try again.';
    case 'NetworkError':
      return 'Cannot reach the sign-in service. Check the connection and try again.';
    default:
      return 'Sign-in failed. Check the connection and try again.';
  }
}
